/**
 * Agent Context - Global state management for agent conversations
 */

import React, { createContext, useContext, useReducer, useEffect } from "react";
import * as agentService from "../services/agentService.js";

const AgentContext = createContext();

// Action types
const ACTIONS = {
  SET_CONVERSATIONS: "SET_CONVERSATIONS",
  SET_CURRENT_CONVERSATION: "SET_CURRENT_CONVERSATION",
  ADD_MESSAGE: "ADD_MESSAGE",
  UPDATE_CONVERSATION: "UPDATE_CONVERSATION",
  SET_STATS: "SET_STATS",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
};

// Initial state
const initialState = {
  conversations: [],
  currentConversation: null,
  stats: {
    pending: 0,
    active: 0,
    closed: 0,
    total: 0,
  },
  loading: false,
  error: null,
};

// Reducer function
const agentReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload,
        loading: false,
        error: null,
      };

    case ACTIONS.SET_CURRENT_CONVERSATION:
      return {
        ...state,
        currentConversation: action.payload,
        loading: false,
        error: null,
      };

    case ACTIONS.ADD_MESSAGE:
      if (!state.currentConversation) return state;

      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          messages: [...state.currentConversation.messages, action.payload],
        },
      };

    case ACTIONS.UPDATE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv.id === action.payload.id ? { ...conv, ...action.payload } : conv
        ),
        currentConversation:
          state.currentConversation?.id === action.payload.id
            ? { ...state.currentConversation, ...action.payload }
            : state.currentConversation,
      };

    case ACTIONS.SET_STATS:
      return {
        ...state,
        stats: action.payload,
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    default:
      return state;
  }
};

// Context provider
export const AgentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(agentReducer, initialState);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    loadStats();
  }, []);

  // Load conversations
  const loadConversations = async (filters = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await agentService.getConversations(filters);
      if (result && result.success) {
        dispatch({
          type: ACTIONS.SET_CONVERSATIONS,
          payload: result.data || [],
        });
      } else {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: result?.error || "Failed to load conversations",
        });
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  // Load conversation with messages
  const loadConversation = async (conversationId) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await agentService.getConversation(conversationId);
      if (result && result.success) {
        dispatch({
          type: ACTIONS.SET_CURRENT_CONVERSATION,
          payload: result.data,
        });
      } else {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: result?.error || "Failed to load conversation",
        });
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  // Send agent message
  const sendMessage = async (conversationId, messageData) => {
    try {
      const result = await agentService.sendAgentMessage(
        conversationId,
        messageData
      );
      if (result && result.success) {
        // Add message to current conversation
        dispatch({
          type: ACTIONS.ADD_MESSAGE,
          payload: result.data,
        });
        return { success: true };
      } else {
        return { success: false, error: result?.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Close conversation
  const closeConversation = async (conversationId, closedBy) => {
    try {
      const result = await agentService.closeConversation(
        conversationId,
        closedBy
      );
      if (result && result.success) {
        dispatch({
          type: ACTIONS.UPDATE_CONVERSATION,
          payload: { id: conversationId, status: "closed" },
        });
        return { success: true };
      } else {
        return { success: false, error: result?.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Assign conversation
  const assignConversation = async (conversationId, agentId) => {
    try {
      const result = await agentService.assignConversation(
        conversationId,
        agentId
      );
      if (result && result.success) {
        dispatch({
          type: ACTIONS.UPDATE_CONVERSATION,
          payload: { id: conversationId, agentId, status: "active" },
        });
        return { success: true };
      } else {
        return { success: false, error: result?.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const result = await agentService.getAgentStats();
      if (result && result.success) {
        dispatch({ type: ACTIONS.SET_STATS, payload: result.data });
      } else {
        console.error(
          "Failed to load agent stats:",
          result?.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to load agent stats:", error);
    }
  };

  // Clear current conversation
  const clearCurrentConversation = () => {
    dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: null });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: null });
  };

  const value = {
    ...state,
    loadConversations,
    loadConversation,
    sendMessage,
    closeConversation,
    assignConversation,
    loadStats,
    clearCurrentConversation,
    clearError,
  };

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
};

// Custom hook to use agent context
export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error("useAgent must be used within an AgentProvider");
  }
  return context;
};
